import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2, ShieldCheck, Clock, XCircle, FileText, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const STYLE = {
  Approved: ["bg-[#D1FAE5] text-[#065F46]", ShieldCheck, "KYC approved"],
  Pending: ["bg-[#FEF3C7] text-[#92400E]", Clock, "Pending verification"],
  Rejected: ["bg-[#FEE2E2] text-[#991B1B]", XCircle, "KYC not approved"],
  "Not submitted": ["bg-[#F4F8F5] text-[#4A6358]", FileText, "KYC not submitted"],
};

export const KycBadge = ({ status = "Not submitted", testid = "kyc-badge" }) => {
  const { t } = useApp();
  const [cls, Icon, label] = STYLE[status] || STYLE["Not submitted"];
  return <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ${cls}`} data-testid={testid}><Icon className="h-3.5 w-3.5" />{t(label)}</span>;
};

export const KycForm = ({ kyc, docs = [], uploadUrl, submitUrl, onDone }) => {
  const { t } = useApp();
  const ref = useRef();
  const [f, setF] = useState({ doc_type: "", doc_number: "", files: [] });
  const [busy, setBusy] = useState(false);
  const status = kyc?.status || "Not submitted";
  const pick = async (e) => {
    const list = [...(e.target.files || [])].slice(0, 4 - f.files.length);
    setBusy(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file, file.name);
        const { data } = await api.post(uploadUrl, fd);
        setF((x) => ({ ...x, files: [...x.files, { ...data, name: file.name }] }));
      }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const submit = async () => {
    if (!f.doc_type) return toast.error(t("Please choose the document type."));
    if (!f.files.length) return toast.error(t("Please upload a photo or PDF of the document."));
    setBusy(true);
    try { await api.post(submitUrl, { ...f, files: f.files.map((x) => x.path) }); toast.success(t("Documents sent for verification")); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="kyc-form">
      <div className="flex flex-wrap items-center gap-2"><KycBadge status={status} />{kyc?.doc_type && <span className="text-xs text-[#4A6358]">{t(kyc.doc_type)}{kyc.doc_number ? ` · ${kyc.doc_number}` : ""}</span>}</div>
      {status === "Rejected" && kyc.reason && <p className="rounded-xl bg-[#FEE2E2] p-3 text-sm text-[#991B1B]" data-testid="kyc-reject-reason">{t("Reason")}: {kyc.reason}</p>}
      {status === "Pending" && <p className="text-sm text-[#4A6358]">{t("Our team is checking your documents. You'll get a notification once it's done.")}</p>}
      {["Not submitted", "Rejected"].includes(status) && (
        <>
          <select value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} data-testid="kyc-doc-type-select"
            className="h-12 w-full rounded-xl border border-[#D2E0D8] bg-white px-3 text-base">
            <option value="">{t("Choose document type")}</option>
            {docs.map((d) => <option key={d} value={d}>{t(d)}</option>)}
          </select>
          <input value={f.doc_number} onChange={(e) => setF({ ...f, doc_number: e.target.value })} placeholder={t("Document number (optional)")}
            className="h-12 w-full rounded-xl border border-[#D2E0D8] bg-white px-3 text-base" data-testid="kyc-doc-number-input" />
          {f.files.map((x, i) => (
            <div key={x.path} className="flex items-center gap-2 rounded-xl bg-[#F4F8F5] px-3 py-2 text-sm" data-testid={`kyc-file-${i}`}>
              <FileText className="h-4 w-4 text-[#2E9E6B]" /><span className="min-w-0 flex-1 truncate">{x.name}</span>
              <button onClick={() => setF((y) => ({ ...y, files: y.files.filter((z) => z.path !== x.path) }))} data-testid={`kyc-file-remove-${i}`}><X className="h-4 w-4 text-[#7B968B]" /></button>
            </div>
          ))}
          <input ref={ref} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={pick} data-testid="kyc-file-input" />
          {f.files.length < 4 && (
            <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid="kyc-upload-btn"
              className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#2E9E6B] bg-white font-semibold text-[#2E9E6B]">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileUp className="h-5 w-5" />}{t("Upload photo or PDF (front & back)")}
            </button>
          )}
          <button onClick={submit} disabled={busy} className="h-14 w-full rounded-2xl bg-[#0D3B2B] text-lg font-bold text-white disabled:opacity-50" data-testid="kyc-submit-btn">{t("Send for verification")}</button>
        </>
      )}
    </div>
  );
};
